import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface FormData {
  volunteerName: string;
  email: string;
  phone: string;
  responseType: 'interested' | 'not_interested' | 'need_info' | '';
  comments: string;
  datePreferences: Record<string, string[]>;
  // Honeypot field - hidden from users, bots will fill it
  website: string;
}

interface StudyImage {
  url: string;
  caption: string;
  order: number;
}

interface CampaignInfo {
  campaignName: string;
  isClosed: boolean;
  interestedCount: number;
  prefill?: {
    name?: string;
    email?: string;
  };
  // Form design media fields
  headerImageUrl?: string;
  studyImages?: string; // JSON string of StudyImage[]
  videoUrl?: string;
  videoType?: 'youtube' | 'vimeo' | 'uploaded';
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const TIME_SLOTS = ['Morning', 'Afternoon', 'Evening'];

// API base URL - configured via environment variable, falls back to localhost for local dev
const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:7071';

// Helper to parse study images JSON
function parseStudyImages(studyImagesJson?: string): StudyImage[] {
  if (!studyImagesJson) return [];
  try {
    const parsed = JSON.parse(studyImagesJson);
    return Array.isArray(parsed) ? parsed.sort((a, b) => a.order - b.order) : [];
  } catch {
    return [];
  }
}

// Helper to get YouTube embed URL
function getYouTubeEmbedUrl(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

// Helper to get Vimeo embed URL
function getVimeoEmbedUrl(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}` : null;
}

function InterestForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL params
  const campaignToken = searchParams.get('campaign') || '';
  const trackingToken = searchParams.get('token') || '';
  const source = (searchParams.get('source') as 'email' | 'facebook' | 'direct') || 'direct';

  // Preselect a radio when the email button passed a response (?response=...).
  // Yes -> interested, No -> not_interested, Find Out More -> need_info.
  const responseParam = searchParams.get('response') || '';
  const initialResponseType: FormData['responseType'] = (
    ['interested', 'not_interested', 'need_info'] as const
  ).includes(responseParam as any)
    ? (responseParam as FormData['responseType'])
    : '';

  const [formData, setFormData] = useState<FormData>({
    volunteerName: '',
    email: '',
    phone: '',
    responseType: initialResponseType,
    comments: '',
    datePreferences: {},
    website: '', // Honeypot - should remain empty
  });

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignValid, setCampaignValid] = useState(false);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [isPrefilled, setIsPrefilled] = useState(false);

  // Fetch campaign info on mount
  useEffect(() => {
    if (!campaignToken) {
      setError('Invalid link. Please use the link from your email.');
      setValidating(false);
      return;
    }

    const fetchCampaignInfo = async () => {
      try {
        const params = new URLSearchParams({ campaign: campaignToken });
        if (trackingToken) {
          params.append('token', trackingToken);
        }

        const response = await fetch(`${API_BASE}/api/getCampaignInfo?${params}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('This campaign could not be found. Please check your link.');
          } else {
            setError('Unable to load campaign information. Please try again later.');
          }
          setValidating(false);
          return;
        }

        const info: CampaignInfo = await response.json();
        setCampaignInfo(info);
        setCampaignValid(true);

        // Autofill name and email if prefill data is available
        if (info.prefill) {
          setFormData((prev) => ({
            ...prev,
            volunteerName: info.prefill?.name || prev.volunteerName,
            email: info.prefill?.email || prev.email,
          }));
          setIsPrefilled(true);
        }
      } catch (err) {
        console.error('Error fetching campaign info:', err);
        setError('Unable to load campaign information. Please try again later.');
      } finally {
        setValidating(false);
      }
    };

    fetchCampaignInfo();
  }, [campaignToken, trackingToken]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResponseTypeChange = (type: FormData['responseType']) => {
    setFormData((prev) => ({ ...prev, responseType: type }));
  };

  const handleAvailabilityChange = (day: string, timeSlot: string, checked: boolean) => {
    setFormData((prev) => {
      const current = prev.datePreferences[day] || [];
      const updated = checked
        ? [...current, timeSlot]
        : current.filter((t) => t !== timeSlot);

      return {
        ...prev,
        datePreferences: {
          ...prev.datePreferences,
          [day]: updated,
        },
      };
    });
  };

  const isAvailabilityChecked = (day: string, timeSlot: string) => {
    return formData.datePreferences[day]?.includes(timeSlot) || false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.volunteerName.trim()) {
      setError('Please enter your name.');
      return;
    }

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!formData.responseType) {
      setError('Please select whether you are interested.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/submitOutreachResponse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          campaignToken,
          trackingToken,
          source,
          volunteerName: formData.volunteerName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined,
          responseType: formData.responseType,
          datePreferences: formData.datePreferences,
          comments: formData.comments.trim() || undefined,
          // Honeypot field - bots will fill this, humans won't see it
          website: formData.website || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      // Success - navigate to thank you page
      navigate('/thank-you', {
        state: {
          responseType: formData.responseType,
          campaignName: result.campaignName,
        },
      });
    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: 16, color: '#fff' }}>Loading...</p>
      </div>
    );
  }

  if (!campaignValid) {
    return (
      <div className="not-found-container">
        <div className="not-found-icon">!</div>
        <h1>Invalid Link</h1>
        <p>{error || 'This form link is invalid or has expired.'}</p>
      </div>
    );
  }

  // Show closed campaign message
  if (campaignInfo?.isClosed) {
    return (
      <div className="closed-container">
        <div className="closed-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        </div>
        <h1>This study is now closed</h1>
        <p className="closed-message">
          Thank you for your interest — keep checking your emails or social media for new opportunities.
        </p>
        {campaignInfo.campaignName && (
          <p className="campaign-name-closed">{campaignInfo.campaignName}</p>
        )}
      </div>
    );
  }

  // Parse study images if available
  const studyImages = parseStudyImages(campaignInfo?.studyImages);

  // Get video embed URL if available
  const getVideoEmbed = () => {
    if (!campaignInfo?.videoUrl) return null;
    if (campaignInfo.videoType === 'youtube') {
      return getYouTubeEmbedUrl(campaignInfo.videoUrl);
    }
    if (campaignInfo.videoType === 'vimeo') {
      return getVimeoEmbedUrl(campaignInfo.videoUrl);
    }
    return null;
  };
  const videoEmbedUrl = getVideoEmbed();

  return (
    <div className="form-container">
      {/* Header Image */}
      {campaignInfo?.headerImageUrl && (
        <div className="campaign-header-image">
          <img src={campaignInfo.headerImageUrl} alt="Study header" />
        </div>
      )}

      <div className="form-header">
        <h1>{campaignInfo?.campaignName || 'Research Study Interest Form'}</h1>
        <p>Let us know if you'd like to participate in this study</p>
        {campaignInfo && campaignInfo.interestedCount > 0 && (
          <div className="interest-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span>{campaignInfo.interestedCount} {campaignInfo.interestedCount === 1 ? 'person has' : 'people have'} already expressed interest</span>
          </div>
        )}
      </div>

      {/* Video Section */}
      {campaignInfo?.videoUrl && (
        <div className="campaign-video">
          {videoEmbedUrl ? (
            <iframe
              src={videoEmbedUrl}
              title="Study video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : campaignInfo.videoType === 'uploaded' ? (
            <video src={campaignInfo.videoUrl} controls />
          ) : null}
        </div>
      )}

      {/* Study Images Gallery */}
      {studyImages.length > 0 && (
        <div className="campaign-gallery">
          {studyImages.map((img, idx) => (
            <div key={idx} className="gallery-item">
              <img src={img.url} alt={img.caption || `Study image ${idx + 1}`} />
              {img.caption && <p className="gallery-caption">{img.caption}</p>}
            </div>
          ))}
        </div>
      )}

      <form className="form-body" onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="volunteerName">
            Full Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="volunteerName"
            name="volunteerName"
            value={formData.volunteerName}
            onChange={handleInputChange}
            placeholder="Enter your full name"
            required
            className={isPrefilled && formData.volunteerName ? 'prefilled' : ''}
          />
          {isPrefilled && formData.volunteerName && (
            <p className="prefill-hint">Pre-filled from your records - you can update if needed</p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">
            Email Address <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your.email@example.com"
            required
            className={isPrefilled && formData.email ? 'prefilled' : ''}
          />
          {isPrefilled && formData.email && (
            <p className="prefill-hint">Pre-filled from your records - you can update if needed</p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="07xxx xxxxxx"
          />
          <p className="hint">Optional - we may call you to discuss the study</p>
        </div>

        {/* Honeypot field - hidden from users, bots will fill it */}
        <div className="honeypot-field" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label>
            Are you interested in participating? <span className="required">*</span>
          </label>
          <div className="response-options">
            <label
              className={`response-option ${formData.responseType === 'interested' ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="responseType"
                value="interested"
                checked={formData.responseType === 'interested'}
                onChange={() => handleResponseTypeChange('interested')}
              />
              <div className="response-option-label">
                <strong>Yes, I'm interested</strong>
                <span>I would like to learn more and potentially participate</span>
              </div>
            </label>

            <label
              className={`response-option ${formData.responseType === 'need_info' ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="responseType"
                value="need_info"
                checked={formData.responseType === 'need_info'}
                onChange={() => handleResponseTypeChange('need_info')}
              />
              <div className="response-option-label">
                <strong>I need more information</strong>
                <span>Please contact me with more details before I decide</span>
              </div>
            </label>

            <label
              className={`response-option ${formData.responseType === 'not_interested' ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name="responseType"
                value="not_interested"
                checked={formData.responseType === 'not_interested'}
                onChange={() => handleResponseTypeChange('not_interested')}
              />
              <div className="response-option-label">
                <strong>No, I'm not interested</strong>
                <span>This study isn't right for me at this time</span>
              </div>
            </label>
          </div>
        </div>

        {(formData.responseType === 'interested' || formData.responseType === 'need_info') && (
          <div className="availability-section">
            <h3>When are you generally available?</h3>
            <p className="hint" style={{ marginBottom: 16 }}>
              Select all times that typically work for you
            </p>
            <div className="availability-grid">
              <div className="header"></div>
              {TIME_SLOTS.map((slot) => (
                <div key={slot} className="header">
                  {slot}
                </div>
              ))}

              {DAYS.map((day) => (
                <>
                  <div key={`${day}-label`} className="day-label">
                    {day}
                  </div>
                  {TIME_SLOTS.map((slot) => (
                    <div key={`${day}-${slot}`} className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={isAvailabilityChecked(day, slot)}
                        onChange={(e) =>
                          handleAvailabilityChange(day, slot, e.target.checked)
                        }
                        aria-label={`${day} ${slot}`}
                      />
                    </div>
                  ))}
                </>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="comments">Additional Comments</label>
          <textarea
            id="comments"
            name="comments"
            value={formData.comments}
            onChange={handleInputChange}
            placeholder="Any questions or information you'd like to share..."
            rows={4}
          />
        </div>

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Response'}
        </button>
      </form>
    </div>
  );
}

export default InterestForm;
